import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const ACCENT = '#4A7C59';

interface BatchSummary {
  id: string;
  batchNumber: string;
  processType: string;
  cherryKg: string | null;
  status: string;
  centreName: string | null;
  createdAt: string;
}

const STATUS_STYLE: Record<string, string> = {
  cherry_intake: 'bg-orange-100 text-orange-700',
  fermentation: 'bg-blue-100 text-blue-700',
  drying: 'bg-yellow-100 text-yellow-700',
  milling: 'bg-purple-100 text-purple-700',
  grading: 'bg-pink-100 text-pink-700',
  complete: 'bg-green-100 text-green-700',
};

const STATUS_LABEL: Record<string, string> = {
  cherry_intake: 'Cherry intake',
  fermentation: 'Fermentation',
  drying: 'Drying',
  milling: 'Milling',
  grading: 'Grading',
  complete: 'Complete',
};

export const InventoryView: React.FC = () => {
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/production/batches?limit=100', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setBatches(d.batches || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group by status
  const grouped = batches.reduce<Record<string, BatchSummary[]>>((acc, b) => {
    (acc[b.status] = acc[b.status] || []).push(b);
    return acc;
  }, {});

  const inProgress = batches.filter(b => b.status !== 'complete');
  const complete = batches.filter(b => b.status === 'complete');

  const totalCherryKg = batches.reduce((sum, b) => sum + (b.cherryKg ? parseFloat(b.cherryKg) : 0), 0);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#1A1A1A]">Inventory</h2>
        <p className="text-sm text-[#6B6B6B] mt-0.5">Batch pipeline overview</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total batches', value: batches.length },
          { label: 'In progress', value: inProgress.length },
          { label: 'Complete', value: complete.length },
          { label: 'Total cherry (kg)', value: totalCherryKg > 0 ? totalCherryKg.toLocaleString() : '—' },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white border border-[#E8E6E2] rounded-xl p-5">
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">{label}</p>
            <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
          </div>
        ))}
      </div>

      {/* Pipeline by stage */}
      {loading ? (
        <div className="py-12 text-center text-sm text-[#6B6B6B]">Loading...</div>
      ) : batches.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-[#6B6B6B] mb-3">No batches in the system yet</p>
          <Link to="/production/batches/new" className="text-sm font-medium" style={{ color: ACCENT }}>Create first batch →</Link>
        </div>
      ) : (
        <div className="space-y-6">
          {['cherry_intake', 'fermentation', 'drying', 'milling', 'grading', 'complete'].map(status => {
            const stageBatches = grouped[status] || [];
            if (stageBatches.length === 0) return null;
            return (
              <div key={status}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[status]}`}>
                    {STATUS_LABEL[status]}
                  </span>
                  <span className="text-sm text-[#6B6B6B]">{stageBatches.length} batch{stageBatches.length !== 1 ? 'es' : ''}</span>
                </div>
                <div className="grid gap-2">
                  {stageBatches.map(b => (
                    <Link
                      key={b.id}
                      to={`/production/batches/${b.id}`}
                      className="bg-white border border-[#E8E6E2] rounded-xl p-4 flex items-center justify-between hover:border-[#4A7C59]/40 transition-colors"
                    >
                      <div>
                        <span className="font-mono text-sm font-medium text-[#1A1A1A]">{b.batchNumber}</span>
                        <div className="flex gap-3 mt-0.5 text-xs text-[#6B6B6B]">
                          <span className="capitalize">{b.processType}</span>
                          {b.cherryKg && <span>{parseFloat(b.cherryKg).toLocaleString()} kg</span>}
                          {b.centreName && <span>{b.centreName}</span>}
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-[#A3A3A3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
